import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Link from 'next/link'
import { connect } from 'react-redux'
import clsx from 'clsx'
import CommonLayout from 'components/layout/common/index'
import Icon from 'components/common/icon'
import ICONS from 'components/common/icon/const'
import Button from 'components/common/button'
import { BUTTON_TYPES } from 'constants/button'
import ResponsiveImage from 'components/images/responsive-image'
import Typo from 'constants/typography'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { withI18next } from 'lib/i18n/withI18next'
import { PAGE_NAME } from 'pages/const'
import BasePage from '../base'
import { ASSETS, CONTENTS } from './const'
import styles from './style.scss'

const About = (props) => {
  const {
    t,
    isPhone,
    internationalization: { shop },
  } = props
  const shouldDisplayTTB = shop !== 15

  const intersectionOption = Object.freeze({
    root: null,
    rootMargin: '0px',
    threshold: 0.5,
  })

  const pomeloBrandHeroRef = useRef()
  const ttbStep1 = useRef()
  const ttbStep2 = useRef()
  const ttbStep3 = useRef()
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('intersect', entry.isIntersecting)

        if (entry.isIntersecting) {
          observer.unobserve(entry.target)
        }
      })
    }, intersectionOption)

    const observedElements = shouldDisplayTTB
      ? [pomeloBrandHeroRef, ttbStep1, ttbStep2, ttbStep3]
      : [pomeloBrandHeroRef]
    observedElements.forEach((element) => {
      observer.observe(element.current)
    })
  }, [intersectionOption, shouldDisplayTTB])

  return (
    <CommonLayout pageName={PAGE_NAME.about} showAdsBanner>
      <style jsx>{styles}</style>
      <section className="aboutus">
        <article className="about">
          <h1 className={clsx('about__title about__title_mobile', Typo.title2)}>
            {hasLocizeTranslation(
              t,
              CONTENTS.ABOUT_TITLE.key,
              CONTENTS.ABOUT_TITLE.value,
            )}
          </h1>
          <ResponsiveImage
            src={ASSETS.HERO_IMAGE}
            imgRatio={0.75}
            alt="Pomelo Fashion"
            className="about__hero-image"
          />
          <div className="about__content-wrapper">
            <h1
              className={clsx('about__title about__title_desktop', Typo.title1)}
            >
              {hasLocizeTranslation(
                t,
                CONTENTS.ABOUT_TITLE.key,
                CONTENTS.ABOUT_TITLE.value,
              )}
            </h1>
            <h2 className={clsx('about__content_1', Typo.body1)}>
              {hasLocizeTranslation(
                t,
                CONTENTS.ABOUT_CONTENT_1.key,
                CONTENTS.ABOUT_CONTENT_1.value,
              )}
            </h2>
            <h2
              className={clsx('about__content_2', Typo.body1)}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: ['Tap.Try.Buy.', 'Pomelo Perks*'].reduce(
                  (text, search) =>
                    text.replace(search, `<strong>${search}</strong>`),
                  hasLocizeTranslation(
                    t,
                    CONTENTS.ABOUT_CONTENT_2.key,
                    CONTENTS.ABOUT_CONTENT_2.value,
                  ),
                ),
              }}
            />
            <Icon
              className="about__icon"
              src={ICONS.pmlLogoMark}
              width={43}
              height={56}
            />
          </div>
        </article>

        <article className="purpose">
          <ResponsiveImage
            src={ASSETS.PURPOSE_BG}
            imgRatio={1}
            alt="Pomelo Fashion"
            className="purpose__hero"
          />
          <div className="purpose__text_wrapper">
            <p
              className={`purpose__title ${
                isPhone ? Typo.title3 : Typo.title2
              }`}
            >
              {hasLocizeTranslation(
                t,
                CONTENTS.PURPOSE_TITLE.key,
                CONTENTS.PURPOSE_TITLE.value,
              )}
            </p>
            <p className={clsx('purpose__content', Typo.subtitle2)}>
              {hasLocizeTranslation(
                t,
                CONTENTS.PURPOSE_CONTENT.key,
                CONTENTS.PURPOSE_CONTENT.value,
              )}
            </p>
            <Link href="/" as="/clothes/new-arrivals/">
              <Button link className="aboutus__cta" type={BUTTON_TYPES.primary}>
                {hasLocizeTranslation(
                  t,
                  CONTENTS.PURPOSE_SHOP_NOW.key,
                  CONTENTS.PURPOSE_SHOP_NOW.value,
                )}
              </Button>
            </Link>
          </div>
        </article>

        <article className="pomelo-brand">
          <h3
            className={`pomelo-brand__title ${
              isPhone ? Typo.title3 : Typo.title1
            }`}
          >
            {hasLocizeTranslation(
              t,
              CONTENTS.POMELO_BRAND_TITLE.key,
              CONTENTS.POMELO_BRAND_TITLE.value,
            )}
          </h3>
          <p
            className={clsx(
              'pomelo-brand__content',
              'pomelo-brand__content_1',
              Typo.body1,
            )}
          >
            {hasLocizeTranslation(
              t,
              CONTENTS.POMELO_BRAND_CONTENT_1.key,
              CONTENTS.POMELO_BRAND_CONTENT_1.value,
            )}
          </p>
          <div
            className="pomelo-brand__hero"
            style={{ backgroundImage: `url(${ASSETS.POMELO_BRAND_BG})` }}
            ref={pomeloBrandHeroRef}
          >
            <ResponsiveImage
              src={ASSETS.POMELO_BRAND_LOGO}
              imgRatio={19.77}
              alt="Pomelo Fashion"
              className="pomelo-brand__hero__logo"
              imgStyle={{ width: isPhone ? 177 : 348 }}
            />
          </div>
          <p
            className={clsx(
              'pomelo-brand__content',
              'pomelo-brand__content_2',
              Typo.body1,
            )}
          >
            {hasLocizeTranslation(
              t,
              CONTENTS.POMELO_BRAND_CONTENT_2.key,
              CONTENTS.POMELO_BRAND_CONTENT_2.value,
            )}
          </p>
          <p
            className={clsx(
              'pomelo-brand__content',
              'pomelo-brand__content_3',
              Typo.body1,
            )}
          >
            {hasLocizeTranslation(
              t,
              CONTENTS.POMELO_BRAND_CONTENT_3.key,
              CONTENTS.POMELO_BRAND_CONTENT_3.value,
            )}
          </p>
        </article>

        {shouldDisplayTTB && (
          <article className="ttb">
            <h3
              className={clsx(
                'ttb__title',
                isPhone ? Typo.title3 : Typo.title1,
              )}
            >
              {hasLocizeTranslation(
                t,
                CONTENTS.TTB_TITLE.key,
                CONTENTS.TTB_TITLE.value,
              )}
            </h3>
            <div className="ttb__steps">
              <ResponsiveImage
                src={ASSETS.TTB_HERO}
                imgRatio={1.4}
                alt="Pomelo Fashion"
                className="ttb__steps__hero"
              />
              <div className="ttb__steps__container">
                <div className="ttb__step" ref={ttbStep1}>
                  <span className={clsx('ttb__step__title', Typo.title1)}>
                    Tap.
                  </span>
                  <p className={clsx('ttb__step__content', Typo.title3)}>
                    {hasLocizeTranslation(
                      t,
                      CONTENTS.TTB_TAP_CONTENT.key,
                      CONTENTS.TTB_TAP_CONTENT.value,
                    )}
                  </p>
                </div>
                <div className="ttb__step" ref={ttbStep2}>
                  <span className={clsx('ttb__step__title', Typo.title1)}>
                    Try.
                  </span>
                  <p className={clsx('ttb__step__content', Typo.title3)}>
                    {hasLocizeTranslation(
                      t,
                      CONTENTS.TTB_TRY_CONTENT.key,
                      CONTENTS.TTB_TRY_CONTENT.value,
                    )}
                  </p>
                </div>
                <div className="ttb__step" ref={ttbStep3}>
                  <span className={clsx('ttb__step__title', Typo.title1)}>
                    Buy.
                  </span>
                  <p className={clsx('ttb__step__content', Typo.title3)}>
                    {hasLocizeTranslation(
                      t,
                      CONTENTS.TTB_BUY_CONTENT.key,
                      CONTENTS.TTB_BUY_CONTENT.value,
                    )}
                  </p>
                </div>
              </div>
            </div>
            <p className={clsx('ttb__content', Typo.body1)}>
              {hasLocizeTranslation(
                t,
                CONTENTS.TTB_CONTENT.key,
                CONTENTS.TTB_CONTENT.value,
              )}
            </p>
            <Link href="/" as="/ttb-home">
              <Button link className="aboutus__cta" type={BUTTON_TYPES.primary}>
                {hasLocizeTranslation(
                  t,
                  CONTENTS.TTB_CTA.key,
                  CONTENTS.TTB_CTA.value,
                )}
              </Button>
            </Link>
          </article>
        )}

        <article className="pomelo-girls">
          <p
            className={clsx('pomelo-girls__content', Typo.body1)}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: ['#PomeloGirls'].reduce(
                (text, search) =>
                  text.replace(search, `<strong>${search}</strong>`),
                hasLocizeTranslation(
                  t,
                  CONTENTS.POMELO_GIRLS_CONTENT.key,
                  CONTENTS.POMELO_GIRLS_CONTENT.value,
                ),
              ),
            }}
          />
          <div className="pomelo-girls__instragram__container">
            {ASSETS.POMELO_GIRLS.map((girl, idx) => (
              <ResponsiveImage
                className="pomelo-girls__instragram__item"
                src={girl}
                imgRatio={1.275}
                key={`pomelo-girls-${idx}`}
              />
            ))}
          </div>
        </article>
      </section>
    </CommonLayout>
  )
}

About.getInitialProps = ({ store, req, res, isServer }) => {
  if (isServer) {
    BasePage.loadInitialContentFromServer({ req, res, store })
  } else {
    BasePage.loadInitialContentFromClient(req, res, store)
  }

  return {}
}

About.defaultProps = {
  internationalization: null,
}

About.propTypes = {
  t: PropTypes.func.isRequired,
  internationalization: PropTypes.shape({
    shop: PropTypes.number,
  }),
  isPhone: PropTypes.bool.isRequired,
}

export { About }

const Extended = withI18next(['global'])(About)

export default connect(
  /* istanbul ignore next */
  (state) => ({
    isPhone: state.device.isPhone,
    internationalization: state.internationalization,
  }),
)(Extended)
